import React, { forwardRef, RefObject } from 'react';
import SmallDonutChartDetail from 'spw-components/charts/small-donut-chart-detail';
import {
  INDIVIDUAL_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  SHARED_SPACES_CATEGORY,
  SHARED_SPACES_KEY,
} from 'spw-constants';
import {
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { hasCustomSubcategory } from 'spw-redux/modules/project-space-type-categories/utils';
import { getColor } from 'spw-shared/utils';
import { PlainWorksheet } from '../../../../models/Worksheet';

interface ISpaceAllocationChartProps {
  worksheet: PlainWorksheet;
  isTotalProgramScenario: boolean;
  individualWorkspacesUsf: number;
  sharedSpacesUsf: number;
  detailedSharedConfig?: boolean;
  height?: string;
  projectColors: IProjectColors;
  totalProgramWorksheets?: PlainWorksheet[];
  individualWorkspacesCategory: IProjectSpaceTypeCategory;
}

const SpaceTypeAllocationUsfChart = forwardRef<
  HTMLElement,
  ISpaceAllocationChartProps
>(
  (
    {
      worksheet,
      individualWorkspacesUsf,
      sharedSpacesUsf,
      detailedSharedConfig,
      height,
      projectColors,
      totalProgramWorksheets,
      individualWorkspacesCategory,
      isTotalProgramScenario,
    },
    ref
  ) => {
    const { getUnit, convertArea } = useUnitConversion();
    const { subtotalProgramUSF } = worksheet;

    const individualWorkspacesPercent = !detailedSharedConfig
      ? (100 - (worksheet.sharedSpacesAssignedSpacePercentage || 0)) / 100
      : individualWorkspacesUsf / subtotalProgramUSF;

    const sharedSpacesPercent = !detailedSharedConfig
      ? worksheet.sharedSpacesAssignedSpacePercentage / 100
      : sharedSpacesUsf / subtotalProgramUSF;

    const data = [
      ['Space Type', 'Count'],
      ['Individual Workspaces', individualWorkspacesUsf || 0],
      ['Shared Spaces', sharedSpacesUsf || 0],
    ];

    const spacesData = [
      {
        quantity: convertArea(individualWorkspacesUsf),
        percent: subtotalProgramUSF ? individualWorkspacesPercent : 0,
        color: getColor(
          individualWorkspacesCategory.defaultSpaceTypeCategory.category,
          projectColors
        ),
        name: individualWorkspacesCategory.name,
        category: INDIVIDUAL_SPACES_CATEGORY,
      },
      {
        quantity: convertArea(sharedSpacesUsf),
        percent: subtotalProgramUSF ? sharedSpacesPercent : 0,
        color: projectColors[SHARED_SPACES_KEY] as string,
        name: 'Shared Spaces',
        category: SHARED_SPACES_CATEGORY,
      },
    ];

    return (
      <div
        ref={ref as RefObject<HTMLDivElement>}
        data-testid="space_type_allocation_usf_chart"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: height || 232,
        }}
      >
        <SmallDonutChartDetail
          isTotalProgramScenario={isTotalProgramScenario}
          totalProgramWorksheets={totalProgramWorksheets}
          style={{ height: height || 232 }}
          title="SPACE TYPE ALLOCATION"
          subtitle={`Subtotal Program ${getUnit('area')}`}
          total={convertArea(subtotalProgramUSF)}
          spacesData={spacesData}
          data={data}
          colors={[
            projectColors[INDIVIDUAL_WORKSPACES_CATEGORY] as string,
            projectColors[SHARED_SPACES_CATEGORY] as string,
          ]}
          showNamesInTable={!detailedSharedConfig}
          spaceTypeNamesStyle={{
            paddingTop: hasCustomSubcategory(individualWorkspacesCategory)
              ? 26
              : 0,
          }}
          categoryListStyle={{ marginBottom: 50, marginTop: 5 }}
          testingPrefix="programming_ps_spacetypealloc_shared"
          anchorRef={ref}
        />
      </div>
    );
  }
);

export default React.memo(SpaceTypeAllocationUsfChart);
